/**
 * Página de Registro — Wizard 2 pasos
 * Step 1: Datos personales / contacto
 * Step 2: Dirección + restaurante + T&C
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { countries, getProvinces } from '../data/locations';
import './AuthPages.css';

const MAX_CONFIRM_ATTEMPTS = 5;
const LOCKOUT_DURATION = 30;

function RegisterPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    user,
    register,
    confirmRegistration,
    login,
    createOrganizationAfterSignup,
    checkUser,
    authLoading,
  } = useAuth();

  const [step, setStep] = useState('account-type'); // 'account-type', 'step1', 'step2', 'confirm', 'creating-org'
  const [accountType, setAccountType] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    cuit: '',
    address: '',
    city: '',
    province: '',
    country: 'Argentina',
    postalCode: '',
    phone: '',
    companyName: '',
    restaurantName: '',
    acceptTerms: false,
  });
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Rate limiting para confirmación
  const [confirmAttempts, setConfirmAttempts] = useState(0);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  // Provinces for selected country
  const availableProvinces = useMemo(() => getProvinces(formData.country), [formData.country]);

  // Auto-generate slug from restaurant name
  const generateSlug = useCallback((name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);
  }, []);

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  // Countdown del lockout
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const timer = setTimeout(() => setLockoutSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [lockoutSeconds]);

  // Reset province when country changes and province is no longer valid
  useEffect(() => {
    if (formData.province && !availableProvinces.includes(formData.province)) {
      setFormData((prev) => ({ ...prev, province: '' }));
    }
  }, [formData.country, formData.province, availableProvinces]);

  const getLockoutDuration = useCallback((attempts) => {
    if (attempts >= 10) return 300;
    if (attempts >= MAX_CONFIRM_ATTEMPTS) return LOCKOUT_DURATION;
    return 0;
  }, []);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: fieldValue };
      if (name === 'restaurantName') {
        next.restaurantSlug = generateSlug(value);
      }
      return next;
    });
    setError('');
  };

  // Formatear CUIT: XX-XXXXXXXX-X
  const handleCuitChange = (e) => {
    let val = e.target.value.replace(/\D/g, '').slice(0, 11);
    if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
    if (val.length > 11) val = val.slice(0, 11) + '-' + val.slice(11);
    setFormData((prev) => ({ ...prev, cuit: val }));
    setError('');
  };

  const validateCUIT = (cuit) => {
    const cuitClean = cuit.replace(/[-\s]/g, '');
    return /^\d{11}$/.test(cuitClean);
  };

  const validatePassword = (password) => {
    if (password.length < 8) return t('register.errors.passwordLength');
    if (!/[A-Z]/.test(password)) return t('register.errors.passwordUppercase');
    if (!/[a-z]/.test(password)) return t('register.errors.passwordLowercase');
    if (!/\d/.test(password)) return t('register.errors.passwordNumber');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return t('register.errors.passwordSpecial');
    return null;
  };

  // Step 1 validation
  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError(t('register.errors.requiredFields'));
      return false;
    }

    if (accountType === 'company' && !formData.companyName) {
      setError(t('register.errors.companyNameRequired'));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return false;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return false;
    }

    if (!formData.cuit || !validateCUIT(formData.cuit)) {
      setError(t('register.errors.invalidCUIT'));
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    setError('');
    if (validateStep1()) {
      setStep('step2');
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.restaurantName.trim()) {
      setError(t('register.errors.restaurantNameRequired'));
      setLoading(false);
      return;
    }

    if (!formData.acceptTerms) {
      setError(t('register.errors.acceptTerms'));
      setLoading(false);
      return;
    }

    try {
      const fullName = `${formData.firstName} ${formData.lastName}`;
      const restaurantSlug = formData.restaurantSlug || generateSlug(formData.restaurantName);
      const userAttributes = {
        phone: formData.phone,
        cuit: formData.cuit,
        address: formData.address,
        city: formData.city,
        province: formData.province,
        country: formData.country,
        postalCode: formData.postalCode,
        accountType: accountType,
        restaurantName: formData.restaurantName,
        restaurantSlug: restaurantSlug,
      };

      if (accountType === 'company') {
        userAttributes.companyName = formData.companyName;
        userAttributes.organizationName = formData.companyName;
      } else {
        userAttributes.organizationName = t('register.defaultOrganizationName');
      }

      const result = await register(formData.email, formData.password, fullName, userAttributes);

      if (result.success) {
        setStep('confirm');
      } else {
        setError(result.error || t('register.errors.registerError'));
      }
    } catch (err) {
      console.error('Error en registro:', err);
      setError(t('register.errors.registerError'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSubmit = async (e) => {
    e.preventDefault();

    if (lockoutSeconds > 0) return;

    setError('');
    setLoading(true);

    if (!confirmationCode) {
      setError(t('register.errors.confirmationCodeRequired'));
      setLoading(false);
      return;
    }

    try {
      const confirmResult = await confirmRegistration(formData.email, confirmationCode);

      if (!confirmResult.success) {
        const newAttempts = confirmAttempts + 1;
        setConfirmAttempts(newAttempts);
        const lockDuration = getLockoutDuration(newAttempts);
        if (lockDuration > 0) {
          setLockoutSeconds(lockDuration);
          setError(t('login.errors.tooManyAttempts', { seconds: lockDuration }));
        } else {
          setError(confirmResult.error || t('register.errors.confirmError'));
        }
        setLoading(false);
        return;
      }

      setStep('creating-org');
      const loginResult = await login(formData.email, formData.password);

      if (!loginResult.success) {
        navigate('/login', {
          state: {
            message: t('register.accountConfirmedLogin'),
            email: formData.email,
          },
        });
        return;
      }

      const orgResult = await createOrganizationAfterSignup();

      if (!orgResult.success) {
        console.error('Error al crear organización:', orgResult.error);
        setError(t('register.errors.orgCreationError'));
        setLoading(false);
        return;
      }

      // Force token refresh so Cognito groups from org creation are included
      await checkUser(true);

      navigate('/', {
        state: { message: t('register.welcomeMessage') },
      });
    } catch (err) {
      console.error('Error en el proceso de confirmación:', err);
      setError(t('register.errors.confirmError'));
      setLoading(false);
    }
  };

  const handleCardKeyDown = (e, type) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setAccountType(type);
      setStep('step1');
    }
  };

  // Step indicator for wizard steps
  const getWizardStepNumber = () => {
    if (step === 'step1') return 1;
    if (step === 'step2') return 2;
    return null;
  };

  const wizardStep = getWizardStepNumber();

  if (authLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Digital Menu</h1>
          <h2>
            {step === 'account-type' && t('register.selectAccountType')}
            {step === 'step1' && t('register.step1Title')}
            {step === 'step2' && t('register.step2Title')}
            {step === 'confirm' && t('register.confirmAccount')}
            {step === 'creating-org' && t('register.settingUpAccount')}
          </h2>
        </div>

        {/* Step indicator */}
        {wizardStep && (
          <div className="register-steps">
            <div className={`register-step ${wizardStep >= 1 ? 'register-step--active' : ''}`}>
              <span className="register-step__number">1</span>
              <span className="register-step__label">{t('register.step1Label')}</span>
            </div>
            <div className="register-step__connector" />
            <div className={`register-step ${wizardStep >= 2 ? 'register-step--active' : ''}`}>
              <span className="register-step__number">2</span>
              <span className="register-step__label">{t('register.step2Label')}</span>
            </div>
          </div>
        )}

        {step === 'creating-org' ? (
          <div className="auth-form">
            <div className="loading-message">
              <p>{t('register.settingUpMessage')}</p>
              <p>{t('register.pleaseWait')}</p>
            </div>
          </div>
        ) : step === 'account-type' ? (
          <div className="auth-form">
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <div
                role="button"
                tabIndex={0}
                aria-label={t('register.selectPerson')}
                onClick={() => {
                  setAccountType('person');
                  setStep('step1');
                }}
                onKeyDown={(e) => handleCardKeyDown(e, 'person')}
                style={{
                  flex: 1,
                  padding: '30px',
                  border: '2px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>👤</div>
                <h3 style={{ margin: '0 0 10px 0' }}>{t('register.person')}</h3>
                <p
                  style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}
                >
                  {t('register.personDescription')}
                </p>
              </div>

              <div
                role="button"
                tabIndex={0}
                aria-label={t('register.selectCompany')}
                onClick={() => {
                  setAccountType('company');
                  setStep('step1');
                }}
                onKeyDown={(e) => handleCardKeyDown(e, 'company')}
                style={{
                  flex: 1,
                  padding: '30px',
                  border: '2px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>🏢</div>
                <h3 style={{ margin: '0 0 10px 0' }}>{t('register.company')}</h3>
                <p
                  style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}
                >
                  {t('register.companyDescription')}
                </p>
              </div>
            </div>

            <div className="auth-footer">
              <p>
                {t('register.alreadyHaveAccount')}{' '}
                <Link to="/login">{t('register.loginHere')}</Link>
              </p>
            </div>
          </div>
        ) : step === 'step1' ? (
          /* ========== STEP 1: Datos personales / contacto ========== */
          <div className="auth-form">
            {/* Tipo de cuenta seleccionado */}
            <div
              style={{
                marginBottom: '4px',
                padding: '10px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <strong>{t('register.accountType')}:</strong>{' '}
              {accountType === 'person' ? t('register.person') : t('register.company')}{' '}
              <button
                type="button"
                onClick={() => setStep('account-type')}
                style={{
                  marginLeft: '10px',
                  color: 'var(--color-primary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {t('register.change')}
              </button>
            </div>

            {/* Nombre y Apellido */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label htmlFor="firstName">{t('register.firstName')} *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder={t('register.firstNamePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">{t('register.lastName')} *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder={t('register.lastNamePlaceholder')}
                />
              </div>
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email">{t('register.email')} *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('register.emailPlaceholder')}
                autoComplete="email"
              />
            </div>

            {/* Contraseñas */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label htmlFor="password">{t('register.password')} *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <small className="form-help">{t('register.passwordHelp')}</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">{t('register.confirmPassword')} *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Teléfono */}
            <div className="form-group">
              <label htmlFor="phone">
                {t('register.phone')}{' '}
                <span
                  style={{
                    color: 'var(--text-tertiary)',
                    fontWeight: 400,
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  ({t('register.optional')})
                </span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+54 11 1234-5678"
                inputMode="tel"
              />
            </div>

            {/* CUIT */}
            <div className="form-group">
              <label htmlFor="cuit">{t('register.cuit')} *</label>
              <input
                type="text"
                id="cuit"
                name="cuit"
                value={formData.cuit}
                onChange={handleCuitChange}
                placeholder="20-12345678-9"
                inputMode="numeric"
              />
            </div>

            {/* Razón Social (solo para empresa) */}
            {accountType === 'company' && (
              <div className="form-group">
                <label htmlFor="companyName">{t('register.companyName')} *</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={t('register.companyNamePlaceholder')}
                />
              </div>
            )}

            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: '0' }}>
              * {t('register.requiredFields')}
            </p>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <button type="button" className="btn btn-primary btn-block" onClick={handleNextStep}>
              {t('register.next')}
            </button>
          </div>
        ) : step === 'step2' ? (
          /* ========== STEP 2: Dirección + T&C ========== */
          <form onSubmit={handleRegisterSubmit} className="auth-form" noValidate>
            {/* País */}
            <div className="form-group">
              <label htmlFor="country">
                {t('register.country')}{' '}
                <span
                  style={{
                    color: 'var(--text-tertiary)',
                    fontWeight: 400,
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  ({t('register.optional')})
                </span>
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid var(--border-color)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--text-base)',
                  backgroundColor: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                }}
              >
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Provincia — dynamic based on country */}
            {availableProvinces.length > 0 ? (
              <div className="form-group">
                <label htmlFor="province">
                  {t('register.province')}{' '}
                  <span
                    style={{
                      color: 'var(--text-tertiary)',
                      fontWeight: 400,
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    ({t('register.optional')})
                  </span>
                </label>
                <select
                  id="province"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-base)',
                    backgroundColor: 'var(--bg-primary)',
                    color: 'var(--text-primary)',
                  }}
                >
                  <option value="">{t('register.selectProvince')}</option>
                  {availableProvinces.map((prov) => (
                    <option key={prov} value={prov}>
                      {prov}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="form-group">
                <label htmlFor="province">
                  {t('register.province')}{' '}
                  <span
                    style={{
                      color: 'var(--text-tertiary)',
                      fontWeight: 400,
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    ({t('register.optional')})
                  </span>
                </label>
                <input
                  type="text"
                  id="province"
                  name="province"
                  value={formData.province}
                  onChange={handleChange}
                  placeholder={t('register.provincePlaceholder')}
                />
              </div>
            )}

            {/* Ciudad */}
            <div className="form-group">
              <label htmlFor="city">
                {t('register.city')}{' '}
                <span
                  style={{
                    color: 'var(--text-tertiary)',
                    fontWeight: 400,
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  ({t('register.optional')})
                </span>
              </label>
              <input
                type="text"
                id="city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder={t('register.cityPlaceholder')}
              />
            </div>

            {/* Dirección y Código Postal */}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label htmlFor="address">
                  {t('register.address')}{' '}
                  <span
                    style={{
                      color: 'var(--text-tertiary)',
                      fontWeight: 400,
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    ({t('register.optional')})
                  </span>
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder={t('register.addressPlaceholder')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="postalCode">
                  {t('register.postalCode')}{' '}
                  <span
                    style={{
                      color: 'var(--text-tertiary)',
                      fontWeight: 400,
                      fontSize: 'var(--text-xs)',
                    }}
                  >
                    ({t('register.optional')})
                  </span>
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleChange}
                  placeholder="1234"
                  inputMode="numeric"
                />
              </div>
            </div>

            {/* Nombre del restaurante */}
            <div className="form-group">
              <label htmlFor="restaurantName">{t('register.restaurantName')} *</label>
              <input
                type="text"
                id="restaurantName"
                name="restaurantName"
                value={formData.restaurantName}
                onChange={handleChange}
                placeholder={t('register.restaurantNamePlaceholder')}
              />
              {formData.restaurantName && (
                <small className="form-help">
                  {t('register.restaurantSlugPreview')}: {generateSlug(formData.restaurantName)}
                </small>
              )}
            </div>

            {/* Términos y condiciones */}
            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                />
                <span>
                  {t('register.acceptTerms')}{' '}
                  <Link to="/terms" target="_blank">
                    {t('register.termsLink')}
                  </Link>
                </span>
              </label>
            </div>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setStep('step1');
                  setError('');
                }}
                style={{ flex: 1 }}
              >
                {t('register.back')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ flex: 2 }}
              >
                {loading ? t('register.registering') : t('register.createAccount')}
              </button>
            </div>
          </form>
        ) : step === 'confirm' ? (
          <form onSubmit={handleConfirmSubmit} className="auth-form" noValidate>
            <p className="confirmation-message">
              {t('register.confirmationSent')} <strong>{formData.email}</strong>.{' '}
              {t('register.enterCode')}
            </p>

            <div className="form-group">
              <label htmlFor="confirmationCode">{t('register.confirmationCode')}</label>
              <input
                type="text"
                id="confirmationCode"
                value={confirmationCode}
                onChange={(e) => {
                  setConfirmationCode(e.target.value);
                  setError('');
                }}
                placeholder="123456"
                maxLength="6"
                inputMode="numeric"
                disabled={lockoutSeconds > 0}
              />
            </div>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading || lockoutSeconds > 0}
            >
              {loading
                ? t('register.confirming')
                : lockoutSeconds > 0
                  ? t('login.lockedOut', { seconds: lockoutSeconds })
                  : t('register.confirmAccount')}
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-block"
              onClick={() => {
                setStep('step2');
                setError('');
              }}
            >
              {t('common.back')}
            </button>
          </form>
        ) : null}

        {step !== 'account-type' && (
          <div className="auth-footer">
            <p>
              {t('register.alreadyHaveAccount')} <Link to="/login">{t('register.loginHere')}</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RegisterPage;
